<template>
  <div class="p-3 border border-border-separator my-1">
    <p class="font-bold text-text-default mb-1">{{ title }}</p>
    <p class="text-text-subtle text-sm mb-2">{{ summary }}</p>

    <!-- Answered, here or elsewhere. The outcome sentence is the server's, never composed here: only
         it knows whether the action actually happened. -->
    <p v-if="answered" class="text-text-default text-sm" data-test="outcome">{{ outcome_line }}</p>

    <!-- FUS-437: a card the server did not mark is shown, never answerable. -->
    <p v-else-if="!actionable" class="text-text-subtle text-xs" data-test="inactive">{{ $t('messaging.confirmationCard.inactive') }}</p>

    <template v-else>
      <p v-if="error_text" class="text-danger text-xs mb-2" data-test="error">{{ error_text }}</p>
      <div class="flex items-center gap-2">
        <button type="button" data-test="confirm" :disabled="resolving"
                class="inline-block text-center text-[var(--text-button-primary)] bg-btn-primary hover:opacity-90 px-3 py-1.5 cursor-pointer disabled:opacity-65 rounded-none"
                @click.stop="confirm">{{ confirm_text }}</button>
        <button type="button" data-test="cancel" :disabled="resolving"
                class="inline-block text-center text-text-default border border-border-separator hover:opacity-90 px-3 py-1.5 cursor-pointer disabled:opacity-65 rounded-none"
                @click.stop="cancel">{{ cancel_text }}</button>
      </div>
    </template>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';

/**
 * The only way an impactful tool call gets performed.
 *
 * Rey can ask to do something that changes data — deleting a report, for instance — but the agent loop
 * cannot carry it out. The call was intercepted at dispatch and parked server-side; this card is the
 * handle on it, and clicking Yes is what actually runs it, over an authenticated endpoint the model
 * cannot reach. Nothing Rey writes, including a turn that claims the user already agreed, can answer
 * one of these.
 *
 * The card is an ordinary chat message, so it reappears on every reload of the thread. That is why it
 * asks the server on mount whether the decision is still open: re-offering a deletion that already
 * happened is the single thing it must never do.
 */
export default {
  name: 'ConfirmationCard',
  props: {
    decisionId: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    summary: {
      type: String,
      required: true,
    },
    confirmLabel: {
      type: String,
      required: false,
      default: '',
    },
    cancelLabel: {
      type: String,
      required: false,
      default: '',
    },
    // FUS-437: false for a card whose message lacks the server's marker — no buttons, no status call.
    actionable: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  emits: [],
  mounted() {
    if (this.actionable)
      this.loadStatus();
  },
  data() {
    return {
      // True once this decision has an answer — from this click, or from one made before a reload.
      answered: false,
      // In flight. Also what stops a second click answering twice.
      resolving: false,
      // The server's sentence about what happened. Never composed locally: only the server knows
      // whether the action succeeded, and a cheerful guess here would be a lie about a deletion.
      outcome_line: '',
      error_text: '',
    };
  },
  computed: {
    ...mapStores(useMainStore),

    /** A blank button is unanswerable, which would strand the decision until it expired. */
    confirm_text() {
      return this.confirmLabel || this.$t('messaging.confirmationCard.yes');
    },

    cancel_text() {
      return this.cancelLabel || this.$t('messaging.confirmationCard.no');
    },
  },
  methods: {
    // Store actions are always reached through a local wrapper, per the project convention.
    getDecisionStatus() {
      return this.mainStore.GetDecisionStatus(this.decisionId);
    },

    resolveDecision(approved) {
      return this.mainStore.ResolveDecision(this.decisionId, approved);
    },

    /**
     * Asks whether the decision is still open, so a reloaded thread does not re-offer an action that
     * has already been taken.
     *
     * A failed read deliberately leaves the buttons available. The endpoint is the real guard — it
     * refuses an already-answered decision on its own — so hiding the buttons here would strand a user
     * who legitimately wants to answer, in exchange for a duplicate-click protection we already have.
     */
    loadStatus() {
      const self = this;
      this.getDecisionStatus().then(response => {
        if (response && response.status && response.status !== 'pending') {
          self.answered = true;
          self.outcome_line = self.$t('messaging.confirmationCard.alreadyAnswered');
        }
      }, () => {});
    },

    confirm() {
      this.answer(true);
    },

    cancel() {
      this.answer(false);
    },

    answer(approved) {
      if (this.resolving || this.answered || !this.actionable) return;

      const self = this;
      this.resolving = true;
      this.error_text = '';
      this.resolveDecision(approved).then(response => {
        self.resolving = false;
        self.answered = true;
        self.outcome_line = (response && response.message)
          ? response.message
          : (approved ? self.$t('messaging.confirmationCard.done') : self.$t('messaging.confirmationCard.cancelled'));
      }, () => {
        // The server consumes a decision only when the call actually reached it, so a failed request
        // leaves this answerable. Presenting it as answered would lose the user their action.
        self.resolving = false;
        self.error_text = self.$t('messaging.confirmationCard.sendFailed');
      });
    },
  },
};
</script>
